import React, { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import api from '../../utils/api';
import { Card, Input, Select, LoadingState, EmptyState, Pagination, Table, THead, TBody, TR, TH, TD } from '../../components/ui';
import { actionBadgeClass } from '../../constants/actionColors';

const FILTERABLE_ACTIONS = ['CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'SUBMIT', 'LOCK', 'UPLOAD', 'ASSIGN', 'WARN', 'CLONE', 'RESOLVE', 'FORCE_SUBMIT'];
const ENTITIES = ['User', 'Exam', 'Question', 'ExamAttempt', 'Violation', 'Message', 'Auth', 'Settings'];
const ROLES = ['SUPER_ADMIN', 'ADMIN', 'PROCTOR', 'STUDENT'];

const AllLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [selectedLog, setSelectedLog] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const fetchLogs = () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 50 });
    if (search) params.set('search', search);
    if (filterAction) params.set('action', filterAction);
    if (filterEntity) params.set('entity', filterEntity);
    if (filterRole) params.set('role', filterRole);
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);

    api.get(`/super-admin/logs?${params.toString()}`)
      .then(res => {
        setLogs(res.data.data || []);
        setPagination(res.data.pagination);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchLogs(); }, [page, search, filterAction, filterEntity, filterRole, dateFrom, dateTo]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold text-white">All Activity Logs</h2>
        <p className="text-slate-500 text-sm mt-1">Complete audit trail — including deleted records</p>
      </div>

      {/* Filters */}
      <Card tone="dark" padding="p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Input
            tone="dark"
            className="col-span-2"
            placeholder="Search logs…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          <Select tone="dark" value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(1); }}>
            <option value="">All Actions</option>
            {FILTERABLE_ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
          </Select>
          <Select tone="dark" value={filterEntity} onChange={e => { setFilterEntity(e.target.value); setPage(1); }}>
            <option value="">All Entities</option>
            {ENTITIES.map(e => <option key={e} value={e}>{e}</option>)}
          </Select>
          <Select tone="dark" value={filterRole} onChange={e => { setFilterRole(e.target.value); setPage(1); }}>
            <option value="">All Roles</option>
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </Select>
          <div className="flex gap-1">
            <Input tone="dark" type="date" className="text-xs" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} />
            <Input tone="dark" type="date" className="text-xs" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} />
          </div>
        </div>
        <p className="text-slate-600 text-xs mt-2">
          {pagination.total} total records
        </p>
      </Card>

      {/* Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        {loading ? (
          <LoadingState tone="dark" label="Loading logs…" className="py-12" />
        ) : (
          <>
            <Table tone="dark">
              <THead>
                <TR>
                  <TH>Action</TH>
                  <TH>Entity</TH>
                  <TH>Description</TH>
                  <TH>User</TH>
                  <TH>Role</TH>
                  <TH>Time</TH>
                  <TH></TH>
                </TR>
              </THead>
              <TBody>
                {logs.map(log => (
                  <React.Fragment key={log._id}>
                    <TR className="cursor-pointer" onClick={() => setSelectedLog(selectedLog?._id === log._id ? null : log)}>
                      <TD>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${actionBadgeClass(log.action, 'dark')}`}>
                          {log.action}
                        </span>
                      </TD>
                      <TD className="text-slate-400 text-xs">{log.entity}</TD>
                      <TD className="max-w-xs truncate">{log.description}</TD>
                      <TD className="text-slate-400 text-xs">{log.userName}</TD>
                      <TD className="text-slate-500 text-xs">{log.userRole}</TD>
                      <TD className="text-slate-600 text-xs whitespace-nowrap">{new Date(log.createdAt).toLocaleString()}</TD>
                      <TD>
                        <ChevronDown className={`w-4 h-4 text-slate-600 transition-transform ${selectedLog?._id === log._id ? 'rotate-180' : ''}`} />
                      </TD>
                    </TR>
                    {selectedLog?._id === log._id && (
                      <TR className="bg-slate-900/80 hover:bg-slate-900/80">
                        <TD colSpan={7}>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs mb-3">
                            <div><span className="text-slate-500">Email:</span> <span className="text-white ml-1">{log.userEmail || '—'}</span></div>
                            <div><span className="text-slate-500">IP:</span> <span className="text-white ml-1 font-mono">{log.ipAddress || '—'}</span></div>
                            <div><span className="text-slate-500">Route:</span> <span className="text-white ml-1 font-mono">{log.method} {log.route}</span></div>
                            <div><span className="text-slate-500">Status:</span> <span className="text-white ml-1">{log.statusCode}</span></div>
                            {log.entityId && <div><span className="text-slate-500">Entity ID:</span> <span className="text-white ml-1 font-mono">{String(log.entityId)}</span></div>}
                            {log.entityName && <div><span className="text-slate-500">Name:</span> <span className="text-white ml-1">{log.entityName}</span></div>}
                          </div>
                          {log.dataSnapshot && (
                            <div className="mb-3">
                              <p className="text-red-400 text-xs font-medium mb-1">Deleted Data Snapshot (preserved):</p>
                              <pre className="text-xs text-slate-300 bg-slate-950 rounded p-3 overflow-auto max-h-48 font-mono border border-slate-800">
                                {JSON.stringify(log.dataSnapshot, null, 2)}
                              </pre>
                            </div>
                          )}
                          {log.changes && (
                            <div className="mb-3">
                              <p className="text-blue-400 text-xs font-medium mb-1">Changes:</p>
                              <pre className="text-xs text-slate-300 bg-slate-950 rounded p-3 overflow-auto max-h-32 font-mono border border-slate-800">
                                {JSON.stringify(log.changes, null, 2)}
                              </pre>
                            </div>
                          )}
                        </TD>
                      </TR>
                    )}
                  </React.Fragment>
                ))}
              </TBody>
            </Table>

            {logs.length === 0 && (
              <EmptyState tone="dark" title="No logs found" description="Try adjusting your filters." />
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800">
                <p className="text-slate-600 text-xs">
                  Page {pagination.page} of {pagination.pages} ({pagination.total} records)
                </p>
                <Pagination tone="dark" variant="numbered" page={page} totalPages={pagination.pages} onChange={setPage} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AllLogs;
